import type { CoinType } from '../cointype/CoinTypeStore';

import type { BattleLeaderboard, BattleLeaderboardRow, BattleQualification } from './types';

/** A percentage held to 0–100, whatever core sent. */
export const clampPct = (value: number) => Math.min(100, Math.max(0, value));

export type BattleLeaderboardView = {
  /** The page as core sent it. The reader's own line stays in its place, to be highlighted there. */
  rows: BattleLeaderboardRow[];
  /** Whose line is the reader's, in `rows` or `pinnedRow`. */
  playerId: number;
  /**
   * The reader's line when they are ranked but not on this page, pinned below the list. Null when the
   * reader is already in `rows`, or unranked (they never played the leading slot).
   */
  pinnedRow: BattleLeaderboardRow | null;
  hasMore: boolean;
};

/**
 * The leaderboard lists only players of the leading slot. The reader's line stays in its place on the
 * page; when they are ranked off the page it is pinned below, as the tournament leaderboard does. A
 * reader who never played the leading slot (`position: null`), or with no leaderboard yet, is not on
 * the list at all: an unranked "-" line read as a broken rank (GS-813).
 */
export function buildLeaderboardView(
  leaderboard: BattleLeaderboard | null,
  playerId: number | null,
  fallbackName: string,
): BattleLeaderboardView {
  const fromPayload = leaderboard?.player ?? null;
  const ownId = fromPayload?.playerId ?? playerId ?? 0;

  // core sends the reader's display name, which is empty for a player with no name on file.
  const asOwnRow = (row: BattleLeaderboardRow): BattleLeaderboardRow => ({
    ...row,
    displayName: row.displayName?.trim() ? row.displayName : fallbackName,
    sharePct: row.sharePct ?? 0,
  });

  const rows = (leaderboard?.rows ?? []).map((row) => (row.playerId === ownId ? asOwnRow(row) : row));
  const isOnPage = rows.some((row) => row.playerId === ownId);

  return {
    rows,
    playerId: ownId,
    pinnedRow: !isOnPage && fromPayload?.position != null ? asOwnRow(fromPayload) : null,
    hasMore: leaderboard?.hasMore ?? false,
  };
}

/**
 * How full the qualify bar is, 0–100. A zero or negative threshold means there is nothing to reach,
 * so the bar reads full; progress past the threshold never overflows it.
 */
export function qualificationPct(qualification: BattleQualification): number {
  const { threshold, progress } = qualification;

  if (!(threshold > 0)) {
    return 100;
  }

  return Math.min(100, Math.max(0, (progress / threshold) * 100));
}

/** What the qualify bar's text reads: `progress/threshold` in the run's coin. */
export type QualificationDisplay = {
  progress: number;
  threshold: number;
  qualified: boolean;
};

/**
 * The smallest step `formatAmountByCoinType` shows at this size. SC goes through `toLocaleString()`,
 * which keeps three decimals. GC goes through `formatValueWithK` in round mode: two decimals below
 * 1K, then one decimal of K (100 GC), two decimals of M (10K GC) and two decimals of B (10M GC).
 */
function displayStep(amount: number, coinType: CoinType): number {
  if (coinType !== 'GC') {
    return 0.001;
  }
  if (amount >= 1_000_000_000) {
    return 10_000_000;
  }
  if (amount >= 1_000_000) {
    return 10_000;
  }
  if (amount >= 1_000) {
    return 100;
  }
  return 0.01;
}

/** `value` floored to a whole number of `step`s, without float noise (1.13 stays 1.13). */
function floorToStep(value: number, step: number, below?: number): number {
  let units = Math.floor(Number((value / step).toFixed(6)));
  if (below !== undefined && units * step >= below) {
    units -= 1;
  }
  return step < 1 ? units / Math.round(1 / step) : units * step;
}

/**
 * The numbers the qualify bar prints. Progress never reads past the threshold, so 5,200 of 3,500
 * reads 3,500/3,500. Short of the threshold it is floored to what the formatter shows, so a
 * formatter that rounds (3,499 GC as "3.5K") cannot read as qualified before the player is.
 * A zero or negative threshold means there is nothing to reach. Scoring is not touched.
 */
export function qualificationDisplay(qualification: BattleQualification, coinType: CoinType): QualificationDisplay {
  const threshold = Math.max(qualification.threshold, 0);
  const qualified = !(qualification.threshold > 0) || qualification.progress >= qualification.threshold;
  const progress = Math.min(Math.max(qualification.progress, 0), threshold);

  if (qualified) {
    return { progress, threshold, qualified };
  }

  return {
    progress: Math.max(floorToStep(progress, displayStep(progress, coinType), threshold), 0),
    threshold,
    qualified,
  };
}
